<template>
    <app-layout title="Detail Dokumen Pengadaan">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                Detail Dokumen Pengadaan
            </h2>
        </template>

        <div class="py-4">
            <div class="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
                <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg">
                    <section class="text-gray-600 body-font">
                        <div class="px-4 py-5 mx-auto">
                            <div class="w-full mb-5">
                                <Accordion :multiple="true" :activeIndex="dokumenPengadaan.status === 'ditolak' ? [0, 1, 2, 3, 4, 5] : [0, 1, 2, 3, 4]">
                                    <!-- Status Section -->
                                    <AccordionTab>
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <span class="font-bold white-space-nowrap">Status</span>
                                            </span>
                                        </template>
                                        <StatusDokumenPengadaanInfo :status="dokumenPengadaan.status" />
                                    </AccordionTab>

                                    <!-- Cancellation Information Section (only show if status is 'ditolak') -->
                                    <AccordionTab v-if="dokumenPengadaan.status === 'ditolak'">
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <i class="pi pi-exclamation-triangle text-red-500"></i>
                                                <span class="font-bold white-space-nowrap text-red-600">Informasi Pembatalan</span>
                                            </span>
                                        </template>
                                        <div class="p-4 bg-red-50 border border-red-200 rounded-lg">
                                            <div class="grid grid-cols-1 gap-4">
                                                <div class="field w-full mb-4">
                                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div class="field">
                                                            <JetLabel value="Waktu Pembatalan" />
                                                            <div class="p-2 border rounded-md bg-white">
                                                                {{ formatDateTime(dokumenPengadaan.waktu_pembatalan) }}
                                                            </div>
                                                        </div>
                                                        <div class="field">
                                                            <JetLabel value="Dibatalkan Oleh" />
                                                            <div class="p-2 border rounded-md bg-white">
                                                                {{ dokumenPengadaan.dibatalkan_oleh ? (dokumenPengadaan.dibatalkan_oleh.fullname || dokumenPengadaan.dibatalkan_oleh.name) : 'N/A' }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="field w-full mb-4">
                                                    <JetLabel value="Keterangan Pembatalan" />
                                                    <div class="p-2 border rounded-md bg-white">
                                                        {{ dokumenPengadaan.keterangan_pembatalan || 'Tidak ada keterangan' }}
                                                    </div>
                                                </div>
                                                <div class="field w-full mb-4" v-if="dokumenPengadaan.path_signatur">
                                                    <JetLabel value="Tanda Tangan Digital" />
                                                    <div class="p-2 border rounded-md bg-white">
                                                        <div v-if="signatureLoading" class="flex items-center justify-center p-4">
                                                            <i class="pi pi-spin pi-spinner text-blue-500"></i>
                                                            <span class="ml-2">Memuat tanda tangan...</span>
                                                        </div>
                                                        <div v-else-if="signatureUrl" class="text-center">
                                                            <img 
                                                                :src="signatureUrl" 
                                                                :alt="dokumenPengadaan.signature || 'Tanda Tangan Digital'"
                                                                class="max-w-full h-auto border border-gray-300 rounded-md shadow-sm mx-auto"
                                                                style="max-height: 300px;"
                                                                @error="onSignatureError"
                                                            />
                                                            <div class="mt-2 text-sm text-gray-600">
                                                                {{ dokumenPengadaan.signature || 'signature.png' }}
                                                            </div>
                                                        </div>
                                                        <div v-else class="flex items-center gap-2 text-red-600">
                                                            <i class="pi pi-exclamation-triangle"></i>
                                                            <span>Gagal memuat tanda tangan</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTab>

                                    <!-- Usulan Pembatalan Pokja -->
                                    <!-- Tampilkan hanya jika ada minimal 1 aktor yang setuju -->
                                    <AccordionTab v-if="pokjaPembatalan && (pokjaPembatalan.actors || []).filter(a => a.is_setuju_pembatalan).length > 0">
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <i class="pi pi-user-edit text-orange-500"></i>
                                                <span class="font-bold white-space-nowrap">Usulan Pembatalan Pokja</span>
                                                <span class="ml-auto bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-xs font-semibold" v-if="pokjaPembatalan">
                                                    {{ (pokjaPembatalan.actors || []).filter(a => a.is_setuju_pembatalan).length }} / {{ pokjaPembatalan.pokja_count }} Setuju
                                                </span>
                                            </span>
                                        </template>
                                        <div class="p-4 lg:w-full md:w-full">
                                            <div v-if="!pokjaPembatalan || (pokjaPembatalan.actors || []).length === 0" class="text-gray-500 italic">
                                                Belum ada usulan pembatalan.
                                            </div>
                                            <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div v-for="actor in pokjaPembatalan.actors" :key="actor.id" class="border rounded-lg p-4 bg-gray-50">
                                                    <div class="flex items-center justify-between mb-2">
                                                        <div class="font-semibold">{{ actor.user_name || 'POKJA' }}</div>
                                                        <span :class="actor.is_setuju_pembatalan ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'" class="px-2 py-1 rounded text-xs">
                                                            {{ actor.is_setuju_pembatalan ? 'Setuju' : 'Belum Setuju' }}
                                                        </span>
                                                    </div>
                                                    <div class="text-sm text-gray-700 whitespace-pre-line mb-2" v-if="actor.alasan_pembatalan">
                                                        {{ actor.alasan_pembatalan }}
                                                    </div>
                                                    <div v-if="actor.signature_url" class="mt-2 bg-white border rounded p-2 text-center">
                                                        <img :src="actor.signature_url" alt="Tanda Tangan" class="max-h-40 inline-block" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTab>

                                    <!-- Paket Pengadaan Section -->
                                    <AccordionTab>
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <span class="font-bold white-space-nowrap">Paket Pengadaan</span>
                                            </span>
                                        </template>
                                        <div class="p-4 lg:w-full md:w-full">
                                            <div class="grid grid-cols-1 gap-4">
                                                <div class="field w-full mb-4">
                                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div class="field">
                                                            <JetLabel value="Nama Paket Pengadaan" />
                                                            <div class="p-2 border rounded-md">
                                                                {{ paketPengadaan.nama_paket_pengadaan }}
                                                            </div>
                                                        </div>
                                                        <div class="field">
                                                            <JetLabel value="Kategori Pengadaan" />
                                                            <div class="p-2 border rounded-md">
                                                                {{ paketPengadaan.kategori_pengadaan }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="field w-full mb-4">
                                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div class="field">
                                                            <JetLabel value="Pagu Anggaran" />
                                                            <div class="p-2 border rounded-md">
                                                                {{ formatCurrency(paketPengadaan.pagu_anggaran) }}
                                                            </div>
                                                        </div>
                                                        <div class="field">
                                                            <JetLabel value="Jenis Pengadaan" />
                                                            <div class="p-2 border rounded-md">
                                                                {{ paketPengadaan.jenis_pengadaan_nama || paketPengadaan.jenis_pengadaan }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="field w-full mb-4">
                                                    <JetLabel value="Ruang Lingkup Pengadaan" />
                                                    <div class="p-2 border rounded-md">
                                                        {{ paketPengadaan.ruang_lingkup_pengadaan }}
                                                    </div>
                                                </div>
                                                <div class="field w-full mb-4">
                                                    <JetLabel value="Spesifikasi Pekerjaan" />
                                                    <div class="p-2 border rounded-md">
                                                        {{ paketPengadaan.spesifikasi_pekerjaan }}
                                                    </div>
                                                </div>
                                                <div class="field w-full mb-4">
                                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div class="field">
                                                            <JetLabel value="Metode Pemilihan" />
                                                            <div class="p-2 border rounded-md">
                                                                {{ paketPengadaan.metode_pemilihan_nama || paketPengadaan.metode_pemilihan }}
                                                            </div>
                                                        </div>
                                                        <div class="field">
                                                            <JetLabel value="MAK" />
                                                            <div class="p-2 border rounded-md">
                                                                {{ paketPengadaan.mak }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTab>

                                    <!-- Jadwal Section -->
                                    <AccordionTab>
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <span class="font-bold white-space-nowrap">Jadwal</span>
                                            </span>
                                        </template>
                                        <div class="p-4 w-full">
                                            <div class="overflow-x-auto">
                                                <DataTable :value="jadwal" responsiveLayout="scroll" class="min-w-full">
                                                    <Column header="Nama Jadwal" class="min-w-[200px]">
                                                        <template #body="slotProps">
                                                            {{ slotProps.data.jadwal?.nama_jadwal || slotProps.data.kode_jadwal }}
                                                        </template>
                                                    </Column>
                                                    <Column field="waktu_mulai" header="Tanggal Mulai" class="min-w-[160px]">
                                                        <template #body="slotProps">
                                                            {{ formatDate(slotProps.data.waktu_mulai) }}
                                                        </template>
                                                    </Column>
                                                    <Column field="waktu_selesai" header="Tanggal Selesai" class="min-w-[160px]">
                                                        <template #body="slotProps">
                                                            {{ formatDate(slotProps.data.waktu_selesai) }}
                                                        </template>
                                                    </Column>
                                                    <Column field="tempat" header="Tempat" class="min-w-[120px]"></Column>
                                                    <Column field="keterangan" header="Keterangan" class="min-w-[150px]"></Column>
                                                </DataTable>
                                            </div>
                                        </div>
                                    </AccordionTab>

                                    <!-- File Dokumen Section -->
                                    <AccordionTab>
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <span class="font-bold white-space-nowrap">File Dokumen</span>
                                            </span>
                                        </template>
                                        <div class="p-4 w-full">
                                            <div class="overflow-x-auto">
                                                <DataTable :value="fileDokumen" responsiveLayout="scroll" class="min-w-full">
                                                    <Column field="nama_file_asli" header="Nama File" class="min-w-[200px]"></Column>
                                                    <Column field="keterangan" header="Keterangan" class="min-w-[150px]"></Column>
                                                    <Column field="mime" header="Tipe File" class="min-w-[120px]"></Column>
                                                    <Column field="size" header="Ukuran" class="min-w-[100px]">
                                                        <template #body="slotProps">
                                                            {{ formatFileSize(slotProps.data.size) }}
                                                        </template>
                                                    </Column>
                                                    <Column header="File Dokumen Prosedur" class="min-w-[200px]">
                                                        <template #body="slotProps">
                                                            {{ slotProps.data.file_dokumen_prosedur ? slotProps.data.file_dokumen_prosedur.nama_file : '-' }}
                                                        </template>
                                                    </Column>
                                                    <Column header="Aksi" class="min-w-[120px]">
                                                        <template #body="slotProps">
                                                            <a :href="'/file/' + slotProps.data.id + '/download'" target="_blank" class="text-blue-600 hover:text-blue-800">
                                                                <Button label="Download" icon="pi pi-download" class="p-button-sm p-button-outlined" />
                                                            </a>
                                                        </template>
                                                    </Column>
                                                </DataTable>
                                            </div>
                                        </div>
                                    </AccordionTab>

                                    <!-- HPS Section -->
                                    <AccordionTab>
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <span class="font-bold white-space-nowrap">HPS (Harga Perkiraan Sendiri)</span>
                                            </span>
                                        </template>
                                        <div class="p-4 w-full">
                                            <div class="mb-4">
                                                <JetLabel value="Total HPS" />
                                                <div class="p-2 border rounded-md font-bold text-lg">
                                                    {{ formatCurrency(dokumenPengadaan.total_hps) }}
                                                </div>
                                            </div>
                                            <div class="overflow-x-auto">
                                                <DataTable :value="hps" responsiveLayout="scroll" class="min-w-full">
                                                    <Column field="urutan" header="No" class="min-w-[60px]"></Column>
                                                    <Column field="nama_barang_jasa" header="Nama Barang/Jasa" class="min-w-[200px]"></Column>
                                                    <Column field="merk" header="Merk" class="min-w-[120px]"></Column>
                                                    <Column field="tipe" header="Tipe" class="min-w-[120px]"></Column>
                                                    <Column field="spesifikasi" header="Spesifikasi" class="min-w-[200px]"></Column>
                                                    <Column field="satuan" header="Satuan" class="min-w-[80px]"></Column>
                                                    <Column field="volume" header="Volume" class="min-w-[80px]"></Column>
                                                    <Column field="harga_satuan" header="Harga Satuan" class="min-w-[140px]">
                                                        <template #body="slotProps">
                                                            {{ formatCurrency(slotProps.data.harga_satuan) }}
                                                        </template>
                                                    </Column>
                                                    <Column header="Total" class="min-w-[140px]">
                                                        <template #body="slotProps">
                                                            {{ formatCurrency(slotProps.data.volume * slotProps.data.harga_satuan) }}
                                                        </template>
                                                    </Column>
                                                    <Column field="catatan" header="Catatan" class="min-w-[150px]"></Column>
                                                </DataTable>
                                            </div>
                                        </div>
                                    </AccordionTab>

                                    <!-- Penawaran Section -->
                                    <AccordionTab>
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <span class="font-bold white-space-nowrap">Penawaran</span>
                                                <span class="ml-auto bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold">
                                                    {{ penawaran ? penawaran.length : 0 }} Penawaran
                                                </span>
                                            </span>
                                        </template>
                                        <div class="p-4 lg:w-full md:w-full">
                                            <div class="flex flex-col justify-center items-center p-4 text-center">
                                                <div class="text-2xl font-bold text-gray-700 mb-2">
                                                    Jumlah Penawaran: {{ penawaran ? penawaran.length : 0 }}
                                                </div>
                                                <div v-if="!penawaran || penawaran.length === 0" class="text-gray-500 italic">
                                                    Belum ada penawaran untuk dokumen pengadaan ini
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTab>
                                </Accordion>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    </app-layout>
</template>

<script>
import { defineComponent } from 'vue';
import AppLayout from '@/Layouts/AppLayout.vue';
import JetLabel from '@/Jetstream/Label';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import StatusDokumenPengadaanInfo from '@/Components/StatusDokumenPengadaanInfo';

export default defineComponent({
    components: {
        AppLayout,
        JetLabel,
        Accordion,
        AccordionTab,
        DataTable,
        Column,
        Button,
        StatusDokumenPengadaanInfo
    },
    props: {
        dokumenPengadaan: Object,
        paketPengadaan: Object,
        jadwal: Array,
        fileDokumen: Array,
        hps: Array,
        penawaran: Array,
        pokjaPembatalan: Object
    },
    data() {
        return {
            signatureUrl: null,
            signatureLoading: false
        };
    },
    mounted() {
        if (this.dokumenPengadaan.path_signatur) {
            this.loadSignature();
        }
    },
    methods: {
        formatDate(dateString) {
            if (!dateString) return '-';
            
            const options = { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            };
            
            return new Date(dateString).toLocaleDateString('id-ID', options);
        },
        formatCurrency(value) {
            if (!value) return 'Rp 0';
            
            return new Intl.NumberFormat('id-ID', {
                style: 'currency',
                currency: 'IDR',
                minimumFractionDigits: 0
            }).format(value);
        },
        formatFileSize(bytes) {
            if (!bytes) return '0 Bytes';
            
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
        formatDateTime(dateString) {
            if (!dateString) return '-';
            
            const options = { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            };
            
            return new Date(dateString).toLocaleDateString('id-ID', options);
        },
        async loadSignature() {
            if (!this.dokumenPengadaan.path_signatur) return;
            
            this.signatureLoading = true;
            try {
                const response = await axios.get(this.route('dokumen-pengadaan.signature', this.dokumenPengadaan.id));
                if (response.data.success) {
                    this.signatureUrl = response.data.signature_url;
                }
            } catch (error) {
                console.error('Error loading signature:', error);
                this.signatureUrl = null;
            } finally {
                this.signatureLoading = false;
            }
        },
        onSignatureError() {
            console.error('Failed to load signature image');
            this.signatureUrl = null;
        },
        downloadSignature() {
            if (this.dokumenPengadaan.path_signatur) {
                window.open(this.route('dokumen-pengadaan.download-signature', this.dokumenPengadaan.id), '_blank');
            }
        }
    }
});
</script>
